import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chirpCues, playChirp, registerChirpGesture } from "./chirp";
import { FALLBACK_BY_STAGE, getVideoClip, type RamonAction, type RamonProps } from "./types";
import { useVideoHandoff } from "./useVideoHandoff";
import { trackApp } from "@/lib/appAnalytics";

export function RamonVideo({ stage, action, item, playbackId, preloadAction, timingSource }: RamonProps) {
  const [reducedMotion, setReducedMotion] = useState(false);
  const intentKey = `${stage}:${item ?? "base"}:${action}:${playbackId ?? 0}`;
  const [completedIntent, setCompletedIntent] = useState<string | null>(null);
  const [promotedAction, setPromotedAction] = useState<RamonAction | null>(null);
  const playingAction = completedIntent === intentKey && stage === "chick" ? "idle" : action;
  const clip = useMemo(() => getVideoClip(stage, playingAction, item), [playingAction, stage, item]);
  const idleClip = useMemo(() => getVideoClip(stage, "idle", item), [stage, item]);
  const candidateClip = useMemo(
    () => {
      if (playingAction !== "idle") return idleClip;
      if (preloadAction) return getVideoClip(stage, preloadAction, item);
      return stage === "chick" ? getVideoClip("chick", "celebrate", item) : idleClip;
    },
    [idleClip, item, playingAction, preloadAction, stage],
  );
  const compatibility = `${stage}:${item ?? "base"}`;
  const requestKey = `${compatibility}:${playingAction}:${playbackId ?? 0}`;

  const handleEnded = useCallback(() => setCompletedIntent(intentKey), [intentKey]);
  const handlePromoted = useCallback(() => setPromotedAction(playingAction), [playingAction]);
  const handoff = useVideoHandoff({
    clip, requestKey, compatibility, preloadClip: candidateClip, enabled: !reducedMotion,
    playbackId, timingSource, action: playingAction,
    onEnded: handleEnded, onPromoted: handlePromoted,
  });

  useEffect(registerChirpGesture, []);
  useEffect(() => {
    const element = handoff.visible === null ? null : handoff.refs[handoff.visible].current;
    if (!element || promotedAction === null || reducedMotion) return;
    const cracking = stage === "hatch" && promotedAction === "hatch";
    const opening = stage === "egg" && promotedAction === "discover";
    const cues = opening ? [0.65] : cracking ? [0] : chirpCues(stage, promotedAction);
    if (!cues.length) return;
    let frame = 0;
    let previous = -1;
    let stop: (() => void) | undefined;
    const fired = new Set<number>();
    const poll = () => {
      const now = element.currentTime;
      if (now < previous) fired.clear();
      if (!element.paused && !element.seeking && !document.hidden) {
        cues.forEach(cue => {
          if (now >= cue && !fired.has(cue)) {
            if (now - cue < 0.22) {
              const playing = playChirp(opening ? "egg-open" : cracking ? "egg-crack" : "chirp", cracking || opening ? now - cue : 0);
              if (playing) { stop?.(); stop = playing; }
            }
            fired.add(cue);
          }
        });
      }
      previous = now;
      frame = requestAnimationFrame(poll);
    };
    frame = requestAnimationFrame(poll);
    const silence = () => stop?.();
    element.addEventListener("pause", silence);
    element.addEventListener("waiting", silence);
    element.addEventListener("seeking", silence);
    document.addEventListener("visibilitychange", silence);
    return () => {
      cancelAnimationFrame(frame); stop?.();
      element.removeEventListener("pause", silence);
      element.removeEventListener("waiting", silence);
      element.removeEventListener("seeking", silence);
      document.removeEventListener("visibilitychange", silence);
    };
  }, [handoff.refs, handoff.visible, promotedAction, reducedMotion, stage]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    let inView = true;
    const sync = () => {
      handoff.refs.forEach((ref, index) => {
        const element = ref.current;
        if (!element) return;
        if (document.hidden || !inView) element.pause();
        else if (!handoff.preparing && index === handoff.visible) void element.play().catch(() => {});
      });
    };
    const target = handoff.refs[0].current;
    const observer = !target || typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(entries => {
      inView = entries[0]?.isIntersecting ?? false; sync();
    }, { threshold: 0.05 });
    if (target) observer?.observe(target);
    document.addEventListener("visibilitychange", sync);
    return () => { observer?.disconnect(); document.removeEventListener("visibilitychange", sync); };
  }, [handoff.preparing, handoff.refs, handoff.visible, reducedMotion]);

  if (reducedMotion) {
    return <img data-media-fallback="reduced-motion" className="ramon-placeholder" src={stage === "chick" ? idleClip.poster : FALLBACK_BY_STAGE[stage]} alt="" aria-hidden="true" />;
  }

  return (
    <div className="ramon-video-stack" aria-hidden="true">
      <img className="ramon-placeholder ramon-video-poster" data-visible={!handoff.visibleCompatible} src={clip.poster || FALLBACK_BY_STAGE[stage]} alt=""
        onError={event => { if (!event.currentTarget.src.endsWith(FALLBACK_BY_STAGE[stage])) event.currentTarget.src = FALLBACK_BY_STAGE[stage]; }} />
      {handoff.slots.map((slot, index) => (
        <video key={index} ref={handoff.refs[index]} className="ramon-video" data-slot={index}
          data-visible={handoff.visible === index && handoff.visibleCompatible} muted playsInline loop={slot.clip.loop} preload="auto"
          onEnded={() => handoff.mediaEnded(index as 0 | 1)}
          onError={() => {
            const element = handoff.refs[index].current;
            if (!element?.error) return;
            trackApp("gema_player_failed",{version:"gema-handoff-v1",source:`media-${element.error.code}`,renderer:"video"});
            handoff.mediaFailed(index as 0 | 1);
          }}>
          <source src={slot.clip.mov} type='video/quicktime; codecs="hvc1"' />
          <source src={slot.clip.webm} type='video/webm; codecs="vp9"' />
        </video>
      ))}
    </div>
  );
}
